import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import Image from "next/legacy/image";
import Link from "@/utils/link";
import useSWR, { SWRConfig } from "swr";
import moment from "moment";

import { getMDXComponent } from "mdx-bundler/client";

import { processMdx } from "@/utils/mdx";
import HomeHeader from "@/components/home/HomeHeader";
import ArticleCard from "@/components/article/ArticleCard";
import { fetchGraphql } from "@/utils/query";
import { imageLoader, removeExt } from "@/utils/image";

import Breadcrumbs from "@/components/common/Breadcrumbs";

import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";
import HomeSearch from "@/components/home/HomeSearch";
import NeedHelpBox from "@/components/home/NeedHelpBox";
import NeedHelpRow from "@/components/home/NeedHelpRow";

const fetcher = (url) => fetch(url).then((res) => res.json());

// Articles that should not be indexed by search engines
const NOINDEX_ARTICLE_IDS = ["Understanding-Assisted-Living-Waiver-ALW-Program-in-CA"];

const CustomLink = (props) => {
    const href = props.href;
    const isInternalLink = href && (href.startsWith("/") || href.startsWith("#"));

    //props.className = "underline text-primary-500 font-bold";

    if (isInternalLink) {
        return (
            <Link href={href}>
                <a {...props} className="underline text-primary-500 font-bold">
                    {props.children}
                </a>
            </Link>
        );
    }

    return <a target="_blank" rel="noopener noreferrer" {...props} />;
};

const CustomImage = ({ src, alt, ...props }) => {
    let newSrc = src;

    if (newSrc.startsWith("https://cdn.purpledoorfinders.com/")) {
        newSrc = removeExt(newSrc);
        newSrc = newSrc + ".sm.jpg";
    }

    const random = Math.random();

    return (
        <div>
            <img src={newSrc} alt={alt} {...props} />
            <div className="mt-2">
                {random > 0.5 ? (
                    <HomeSearch
                        inputClassName="w-[calc(100%-64px)] max-w-[500px]"
                        enableLocation={true}
                        higherInput={false}
                    />
                ) : (
                    <NeedHelpRow />
                )}
            </div>
        </div>
    );
};

const MDXComponents = {
    Image,
    a: CustomLink,
    img: CustomImage,
};

const Article = ({ fallback, articleId }) => {
    return (
        <SWRConfig value={{ fallback }}>
            <ArticleWithInitialData articleId={articleId} />
        </SWRConfig>
    );
};

const ArticleWithInitialData = ({ articleId }) => {
    const router = useRouter();
    //const path = router.pathname.split("/");
    //const articleId = path[path.length - 1];
    const { data } = useSWR("/api/article/" + articleId, fetcher);

    const {
        code,
        frontMatter,
        title,
        date,
        authorName,
        mainImageUrl,
        topic,
        description,
        articleLink,
        fAQLink,
    } = data;

    if (!code) {
        // return an error page with article not found
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-4xl font-bold text-red-500">Article not found</div>
            </div>
        );
    }

    const Component = useMemo(() => getMDXComponent(code), [code]);

    return (
        <div className="bg-gray-50">
            <HomeHeader fixed />

            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16 max-w-7xl">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        { name: "Articles", href: "/resources/articles" },
                        {
                            name: title,
                            href: "/resources/articles/" + articleId,
                            hideMobile: true,
                        },
                    ]}
                />
            </div>

            <div className="relative h-48 sm:h-[500px]">
                {mainImageUrl ? (
                    <div className="container mx-auto px-4 sm:px-16 max-w-7xl h-48 sm:h-[500px]">
                        <div className="relative h-48 sm:h-[500px]">
                            <Image
                                loader={imageLoader}
                                src={removeExt(mainImageUrl) + ".md.jpg"}
                                alt={title}
                                layout="fill"
                                objectFit="cover"
                                objectPosition="center"
                                priority
                                unoptimized={false}
                                className=""
                            />
                        </div>
                    </div>
                ) : null}

                <div className="hidden sm:block absolute z-10 bottom-8 left-1/2 transform -translate-x-1/2 py-8 px-8 w-[800px] bg-gray-50/90 rounded">
                    <div>
                        <h3 className="text-xl text-primary-500 font-headline font-bold uppercase text-center">
                            {topic &&
                                topic.split(";").map((k, i) => (
                                    <span key={k}>
                                        {i > 0 ? (
                                            <span className="mx-2">&bull;</span>
                                        ) : null}
                                        {k}
                                    </span>
                                ))}
                        </h3>

                        <h2 className="mt-2 text-3xl font-headline font-bold text-center">
                            {title}
                        </h2>

                        <div className="mt-2 text-gray-700 text-center">
                            <span>Posted {moment(date).format("MM/DD/YYYY")}</span>
                            <span className="mx-2">&bull;</span>

                            {authorName && (
                                <span>
                                    By {authorName}
                                    <span className="mx-2">&bull;</span>
                                </span>
                            )}

                            {frontMatter &&
                            frontMatter.readingTime &&
                            frontMatter.readingTime.text ? (
                                <span>{frontMatter.readingTime.text}</span>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            <div className="pt-8 pb-8 container mx-auto px-4 sm:px-16">
                <NeedHelpBox />
            </div>

            <div className="sm:hidden mt-8">
                <h3 className="text-xl text-primary-500 font-headline font-bold uppercase text-center">
                    {topic &&
                        topic.split(";").map((k, i) => (
                            <span key={k}>
                                {i > 0 ? <span className="mx-2">&bull;</span> : null}
                                {k}
                            </span>
                        ))}
                </h3>

                <h2 className="mt-2 text-3xl font-headline font-bold text-center">
                    {title}
                </h2>

                <div className="mt-2 text-gray-700 text-center">
                    <span>Posted {moment(date).format("MM/DD/YYYY")}</span>
                    <span className="mx-2">&bull;</span>

                    {authorName && (
                        <span>
                            By {authorName}
                            <span className="mx-2">&bull;</span>
                        </span>
                    )}

                    {frontMatter &&
                    frontMatter.readingTime &&
                    frontMatter.readingTime.text ? (
                        <span>{frontMatter.readingTime.text}</span>
                    ) : null}
                </div>
            </div>

            <div className="mt-8 container mx-auto px-4 sm:px-16 max-w-7xl">
                <div className="mt-2 mb-2">
                    <div className="mt-6 flex flex-col sm:flex-row sm:justify-between sm:space-x-8">
                        <div className="">
                            {description ? (
                                <div className="mt-4 text-lg">
                                    {description}
                                    <hr className="mt-2" />
                                </div>
                            ) : null}

                            <div className="mt-4 mb-8 prose max-w-3xl">
                                <Component components={MDXComponents} />
                            </div>
                        </div>

                        <div className="sm:flex-shrink-0 sm:w-72">
                            <h2 className="text-xl text-gray-700 font-headline">
                                Related Articles
                            </h2>

                            <div className="mt-4 flex flex-col space-y-8">
                                {articleLink
                                    ? articleLink
                                          .filter((r) => r.relatedArticle_link !== null)
                                          .map((r, index) => (
                                              <ArticleCard
                                                  key={
                                                      r.relatedArticle_link
                                                          ? r.relatedArticle_link.title
                                                          : index
                                                  }
                                                  article={r.relatedArticle_link}
                                              />
                                          ))
                                    : null}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-4xl">
                    <div>
                        <h2 className="mt-8 sm:mt-0 mb-4 text-xl font-bold">FAQ</h2>

                        {fAQLink
                            ? fAQLink
                                  .filter((f) => f.faqLink_link !== null)
                                  .map((f) => f.faqLink_link)
                                  .map((f) => (
                                      <div
                                          key={f.question}
                                          className="mt-4 px-4 py-4 bg-gray-200 rounded-md"
                                      >
                                          <h3 className="font-bold">{f.question}</h3>
                                          <p className="mt-2">{f.answer}</p>
                                      </div>
                                  ))
                            : null}

                        <div className="px-4 py-4">
                            <Link href="/resources/faq">
                                <a className="text-primary-500 hover:underline">
                                    More Questions
                                </a>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="z-10">
                <SeoLinks />
                <HomeFooter />
            </div>
        </div>
    );
};

// export const getStaticPaths = async () => {
//     const result = await fetchGraphql(`
//         query {
//             articles {
//                 articleId
//             }
//         }
//     `);

//     const articles =
//         result && result.data && result.data.articles && result.data.articles.length > 0
//             ? result.data.articles
//             : [];

//     let paths = articles.map((article) => ({
//         params: { articleId: article.articleId },
//     }));

//     return {
//         paths,
//         fallback: "blocking",
//     };
// };

//export const getStaticProps = async ({ params }) => {
export const getServerSideProps = async (context) => {
    // get the article id from the params
    const { params } = context;

    const result = await fetchGraphql(
        `
        query($find: JSON) {
            articles(find: $find) {
                articleId
                title
                date
                description
                content
                mainImageUrl
                topic
                author_link {
                    name
                }
                articleLink {
                    relatedArticle_link {
                        articleId
                        title
                        mainImageUrl
                        topic
                    }
                }
                fAQLink {
                    faqLink_link {
                        question
                        answer
                        topic
                        question1
                        question2
                        question3
                    }
                }
            }
        }
    `,
        {
            find: {
                articleId: params.articleId,
            },
        }
    );

    let article = null;
    if (result && result.data && result.data.articles && result.data.articles.length > 0) {
        article = result.data.articles[0];
    } else {
        return {
            props: {
                articleId: params.articleId,
                fallback: {
                    ["/api/article/" + params.articleId]: {
                        code: "",
                        frontMatter: {},
                        title: "",
                        date: "",
                        authorName: "",
                        mainImageUrl: "",
                        topic: "",
                        description: "",
                        articleLink: [],
                        fAQLink: [],
                    },
                },
            },
        };
    }

    let post;
    if (article.content) {
        post = await processMdx(
            article.content.replaceAll("\n", "  \n").replaceAll("<br>", "  \n")
        );
    } else {
        post = { code: "", frontMatter: {} };
    }

    const { code, frontMatter } = post;

    const jsonLd = {
        "@context": "http://schema.org",
        "@type": "Article",
        name: article.title,
        author: {
            "@type": "Person",
            name:
                article.author_link && article.author_link.name
                    ? article.author_link.name
                    : "Admin User",
        },
        datePublished: article.date,
        image: article.mainImageUrl ? removeExt(article.mainImageUrl) + ".md.jpg" : null,
    };

    return {
        props: {
            articleId: params.articleId,
            fallback: {
                ["/api/article/" + params.articleId]: {
                    code,
                    frontMatter,
                    title: article.title,
                    date: article.date,
                    authorName:
                        article.author_link && article.author_link.name
                            ? article.author_link.name
                            : "",
                    mainImageUrl: article.mainImageUrl,
                    topic: article.topic,
                    description: article.description,
                    articleLink: article.articleLink,
                    fAQLink: article.fAQLink,
                },
            },
            meta: {
                title: article.title + " | Purple Door",
                description: article.description,
                jsonLd,
                ogTitle: article.title + " | Purple Door",
                ogImage: article.mainImageUrl
                    ? removeExt(article.mainImageUrl) + ".md.jpg"
                    : null,
                ogType: "article",
                robots: NOINDEX_ARTICLE_IDS.includes(params.articleId)
                    ? "noindex,nofollow"
                    : null,
            },
        },
    };
};

export default Article;
